/**
 * ImpactPanel — post-save impact analysis for the multi-agent run.
 *
 * PORTED from pages/dev-agent/components/rightPanels/ImpactPanel.tsx with:
 *   - useDevAgentStore + FeedItem[]   -> useMultiAgentIdeStore.impactAnalysis
 *     (the typed `ImpactAnalysisEntry[]` slice; multi-agent has no feedItems)
 *   - addFeedItem(markdown) on click  -> onSendMessage(...) to the chat
 *   - dev-agent getMetadataItemByName -> local resolver (resolveDependencyItemForEditor)
 * Colours / layout are byte-identical to the dev-agent panel.
 */
import { Zap } from "lucide-react";
import {
  useMultiAgentIdeStore,
  type ImpactAnalysisEntry,
} from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { PanelSection } from "./PanelSection";
import { getMetadataItemByName } from "./resolveDependencyItemForEditor";

function callerCount(entry: ImpactAnalysisEntry): number {
  return Array.isArray(entry.callers) ? entry.callers.length : 0;
}

function riskLevel(entry: ImpactAnalysisEntry): "high" | "medium" | "low" {
  const blast = entry.blast_radius ?? 0;
  return blast > 10 ? "high" : blast > 3 ? "medium" : "low";
}

function hasImpactData(entry: ImpactAnalysisEntry): boolean {
  return Boolean(
    entry.summary ||
      callerCount(entry) > 0 ||
      (entry.blast_radius != null && entry.blast_radius > 0) ||
      (Array.isArray(entry.data_access) && entry.data_access.length > 0) ||
      (Array.isArray(entry.triggers_at_risk) && entry.triggers_at_risk.length > 0),
  );
}

function buildImpactMarkdown(entry: ImpactAnalysisEntry): string {
  const lines = [`## Impact Analysis: ${entry.class_name}`, ""];
  if (entry.summary) lines.push(entry.summary, "");
  if (callerCount(entry) > 0 || entry.blast_radius != null) {
    lines.push("| Type | Count |", "|---|---|");
    if (entry.blast_radius != null) lines.push(`| Blast Radius | ${entry.blast_radius} |`);
    lines.push(`| Callers | ${callerCount(entry)} |`);
  }
  if (Array.isArray(entry.callers) && entry.callers.length > 0) {
    lines.push("", "| Caller | Type | Risk |", "|---|---|---|");
    entry.callers.forEach((caller) => {
      lines.push(`| ${caller.name} | ${caller.type} | ${caller.risk} |`);
    });
  }
  if (Array.isArray(entry.data_access) && entry.data_access.length > 0) {
    lines.push("", "| Object | Operations |", "|---|---|");
    entry.data_access.forEach((d) => {
      lines.push(`| ${d.object} | ${(d.operations || []).join(", ")} |`);
    });
  }
  if (Array.isArray(entry.triggers_at_risk) && entry.triggers_at_risk.length > 0) {
    lines.push(
      "",
      `**Triggers at risk:** ${entry.triggers_at_risk.map((t) => t.trigger).join(", ")}`,
    );
  }
  return lines.join("\n");
}

export function ImpactPanel({
  analyzingFile,
  isBusy,
  onMoveUp,
  onMoveDown,
  onSendMessage,
  onOpenFileInEditor,
}: {
  analyzingFile?: string | null;
  isBusy?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onSendMessage: (msg: string, displayText?: string) => void;
  onOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
}) {
  const impactAnalysis = useMultiAgentIdeStore((s) => s.impactAnalysis);
  const clearImpactAnalysis = useMultiAgentIdeStore((s) => s.clearImpactAnalysis);
  // Newest first, like the dev-agent dedupeByClassName(reverse) output.
  const uniqueItems = [...impactAnalysis].reverse();

  return (
    <PanelSection
      disabled={isBusy}
      title="Impact Analysis"
      icon={<Zap className="w-3.5 h-3.5" />}
      count={impactAnalysis.length}
      onClear={impactAnalysis.length > 0 ? clearImpactAnalysis : undefined}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    >
      {uniqueItems.length > 0 ? (
        <div className="space-y-1">
          {uniqueItems.map((entry) => {
            const risk = riskLevel(entry);
            const callers = callerCount(entry);
            const clickable = hasImpactData(entry)
              ? () => {
                  // Multi-agent has no feed: post the impact markdown to chat
                  // as a display-only bubble so the user can read it inline.
                  onSendMessage(buildImpactMarkdown(entry), buildImpactMarkdown(entry));
                }
              : undefined;
            return (
              <div
                key={entry.class_name}
                className={`w-full text-left py-1.5 px-2 rounded transition-colors group ${clickable ? "hover:bg-muted/50 cursor-pointer" : "cursor-default opacity-80"}`}
                onClick={clickable}
              >
                {entry.class_name && (
                  <div className="font-semibold text-xs text-foreground">
                    {entry.class_name}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  {entry.blast_radius != null && (
                    <span className="text-[10px] text-muted-foreground font-medium">
                      {entry.blast_radius} dependents
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground font-medium">
                    {callers} callers
                  </span>
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                      risk === "high"
                        ? "bg-red-500/25 text-red-800 border-red-600/40 dark:text-red-200 dark:border-red-400/50"
                        : risk === "medium"
                          ? "bg-amber-500/25 text-amber-900 border-amber-600/40 dark:text-amber-200 dark:border-amber-400/50"
                          : "bg-emerald-500/25 text-emerald-900 border-emerald-600/40 dark:text-emerald-200 dark:border-emerald-400/50"
                    }`}
                  >
                    {risk}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 mt-1.5">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSendMessage(`Show me the full dependency tree for ${entry.class_name}`);
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Dependencies
                  </button>
                  <span className="text-[10px] text-muted-foreground select-none" aria-hidden>
                    {"·"}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSendMessage(`What would break if I change ${entry.class_name}?`);
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    What breaks?
                  </button>
                  <span className="text-[10px] text-muted-foreground select-none" aria-hidden>
                    {"·"}
                  </span>
                  <button
                    type="button"
                    onClick={async (event) => {
                      event.stopPropagation();
                      let fileId = entry.class_name;
                      try {
                        const orgId = (await import("@/stores/authStore_v1")).useAuthStoreV1.getState().selectedOrg?.id;
                        const item = await getMetadataItemByName(entry.class_name, orgId, true);
                        if (item?.id) fileId = String(item.id);
                      } catch {
                        /* fallback to class name */
                      }
                      onOpenFileInEditor({
                        id: fileId,
                        name: entry.class_name,
                        metadataType: "ApexClass",
                      });
                    }}
                    className="text-[10px] font-medium text-blue-700 dark:text-blue-400 hover:underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    Open
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : analyzingFile ? (
        <div className="flex items-center gap-2 justify-center py-2 text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-primary border-t-transparent" />
          <span className="text-xs font-medium">Analyzing {analyzingFile}...</span>
        </div>
      ) : (
        <div className="text-muted-foreground text-center py-2">
          No impact data yet
        </div>
      )}
    </PanelSection>
  );
}
